import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import BackButton from "../../../shared/components/Backbtn";
import PageLayout from "../../../shared/components/PageLayout";
import "./post.css";
import {
  collection,
  doc,
  deleteDoc,
  query,
  orderBy,
  updateDoc,
  arrayUnion,
  arrayRemove,
  limit,
  onSnapshot,
  getCountFromServer,
} from "firebase/firestore";
import { useFirebase } from "../../../context/Firebase";
import { useNavigate } from "react-router-dom";
import { IconButton, Button, Avatar, Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareIcon from "@mui/icons-material/Share";
import BlockIcon from "@mui/icons-material/Block";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../../context/Firebase";
import { getPostContent, renderContentWithHashtags, getPostImages, isHashtagBlocked } from "../../../shared/utils/postHelpers";
import { getDefaultImage } from "../../../shared/utils/imageHelpers";
import HashtagSearch from "./HashtagSearch";

const POSTS_LIMIT = 20;

const Post = () => {
  const { db } = useFirebase();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentCounts, setCommentCounts] = useState({});
  const [authUser] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  const [blockedDialog, setBlockedDialog] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleHashtagClick = useCallback(async (hashtag) => {
    const tag = hashtag.replace(/^#/, "").toLowerCase();
    const blocked = await isHashtagBlocked(tag);
    if (blocked) {
      setBlockedDialog(true);
    } else {
      setShowSearch(true);
      // Pre-fill search with clicked hashtag — handled via navigate or state
    }
  }, []);

  const updateCommentCountsAsync = useCallback(async (posts) => {
    const counts = {};
    for (const post of posts) {
      try {
        const commentRef = collection(db, "posts", post.id, "comments");
        const snapshot = await getCountFromServer(commentRef);
        counts[post.id] = snapshot.data().count;
      } catch {
        counts[post.id] = 0;
      }
    }
    setCommentCounts((prev) => ({ ...prev, ...counts }));
  }, [db]);

  // Real-time listener — new posts appear instantly
  // Uses createdAt for ordering; legacy posts missing this field are excluded by Firestore.
  // The date display below falls back to the legacy `createAt` field for those documents.
  useEffect(() => {
    const q = query(
      collection(db, "posts"),
      orderBy("createdAt", "desc"),
      limit(POSTS_LIMIT)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const posts = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setData(posts);
      setLoading(false);
      updateCommentCountsAsync(posts);
    }, (err) => {
      console.error("Error listening to posts:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, updateCommentCountsAsync]);

  const handleDelete = async (id) => {
    if (window.confirm("Delete this post?")) {
      try {
        await deleteDoc(doc(db, "posts", id));
        // onSnapshot will automatically remove it from state
      } catch (err) {
        console.error("Error deleting post:", err);
      }
    }
  };

  const toggleLike = async (postId, postLikes) => {
    if (!authUser) return;
    const postRef = doc(db, "posts", postId);
    const isLiked = postLikes?.includes(authUser.uid);

    // Optimistic update
    setData(prev => prev.map(p =>
      p.id === postId
        ? { ...p, likes: isLiked
            ? p.likes.filter(id => id !== authUser.uid)
            : [...(p.likes || []), authUser.uid]
          }
        : p
    ));

    await updateDoc(postRef, {
      likes: isLiked ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid),
    });
  };

  const [lightboxUrl, setLightboxUrl] = useState(null);

  const handleNavigateToComments = (postId) => {
    navigate(`/comments/${postId}`);
  };

  if (loading) {
    return (
      <div className="loader">
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content" style={{ backgroundColor: 'var(--app-body-bg)' }}>
        <PageLayout
          title="Posts"
          actions={
            <>
              <BackButton tooltip="Back" />
              <Button variant="outlined" onClick={() => setShowSearch(s => !s)}>
                {showSearch ? "Hide Search" : "Search Hashtags"}
              </Button>
              <Button variant="outlined" color="warning" onClick={() => navigate("/manage-hashtags")}>
                Manage Hashtags
              </Button>
              <Button variant="contained" color="primary" onClick={() => navigate("/addpost")}>
                Add New Post
              </Button>
            </>
          }
        >

        {showSearch && <HashtagSearch />}

        {/* Blocked Hashtag Dialog */}
        <Dialog open={blockedDialog} onClose={() => setBlockedDialog(false)}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BlockIcon color="error" /> Hashtag Unavailable
          </DialogTitle>
          <DialogContent>
            This hashtag is not available due to community guidelines.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockedDialog(false)} variant="contained">OK</Button>
          </DialogActions>
        </Dialog>

        {/* Lightbox */}
        {lightboxUrl && (
          <div
            onClick={() => setLightboxUrl(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, cursor: 'zoom-out'
            }}
          >
            <img
              src={lightboxUrl}
              alt="Full view"
              style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '4px' }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          {data.map((post) => {
            const postContent = getPostContent(post);
            const postImages = getPostImages(post);
            const isLiked = post.likes?.includes(authUser?.uid);

            return (
              <div
                key={post.id}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color)'
                }}
              >
                {/* Post Header */}
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Avatar
                      src={post.userProfilePic || post.myUser?.picture || (post.userGender ? getDefaultImage(post.userGender) : null)}
                      sx={{ width: 40, height: 40 }}
                    >
                      {(post.userName || post.myUser?.name)?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>
                        {post.userName || post.myUser?.name || "Unknown User"}
                      </div>
                      <div style={{ fontSize: '13px', color: '#65676b' }}>
                        {post.createdAt?.seconds
                          ? new Date(post.createdAt.seconds * 1000).toLocaleDateString()
                          : post.createAt?.seconds
                          ? new Date(post.createAt.seconds * 1000).toLocaleDateString()
                          : 'Just now'}
                      </div>
                    </div>
                  </div>
                  <IconButton size="small" onClick={() => handleDelete(post.id)}>
                    <MoreVertIcon />
                  </IconButton>
                </div>

                {/* Post Content */}
                {postContent && (
                  <div style={{ padding: '0 16px 12px', fontSize: '15px', lineHeight: '1.3333' }}>
                    {renderContentWithHashtags(postContent, handleHashtagClick)}
                  </div>
                )}

                {/* Post Images */}
                {postImages.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gap: '2px',
                    gridTemplateColumns: postImages.length === 1 ? '1fr' : '1fr 1fr',
                  }}>
                    {postImages.slice(0, 4).map((url, imgIndex) => (
                      <div
                        key={imgIndex}
                        style={{
                          position: 'relative',
                          gridColumn: postImages.length === 3 && imgIndex === 0 ? 'span 2' : 'auto',
                          aspectRatio: postImages.length === 1 ? 'unset' : '1/1',
                          overflow: 'hidden',
                          backgroundColor: '#f0f2f5',
                          cursor: post.isVideo ? 'default' : 'zoom-in',
                        }}
                        onClick={() => !post.isVideo && setLightboxUrl(url)}
                      >
                        {post.isVideo ? (
                          <video
                            controls
                            src={url}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <img
                            src={url}
                            alt={`post-${imgIndex}`}
                            style={{
                              width: '100%',
                              height: postImages.length === 1 ? 'auto' : '100%',
                              maxHeight: postImages.length === 1 ? '500px' : 'none',
                              objectFit: postImages.length === 1 ? 'contain' : 'cover',
                              display: 'block'
                            }}
                          />
                        )}
                        {postImages.length > 4 && imgIndex === 3 && (
                          <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontSize: '32px', fontWeight: 600
                          }}>
                            +{postImages.length - 4}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Post Stats */}
                <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', color: '#65676b' }}>
                  <div>{post.likes?.length || 0} {post.likes?.length === 1 ? 'like' : 'likes'}</div>
                  <div>{commentCounts[post.id] || 0} {commentCounts[post.id] === 1 ? 'comment' : 'comments'}</div>
                </div>

                {/* Post Actions */}
                <div style={{ borderTop: '1px solid #e4e6eb', padding: '4px 16px', display: 'flex', justifyContent: 'space-around' }}>
                  <Button
                    startIcon={isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    onClick={() => toggleLike(post.id, post.likes)}
                    sx={{ color: isLiked ? '#e4405f' : '#65676b', textTransform: 'none', fontWeight: 600, flex: 1 }}
                  >
                    Like
                  </Button>
                  <Button
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => handleNavigateToComments(post.id)}
                    sx={{ color: '#65676b', textTransform: 'none', fontWeight: 600, flex: 1 }}
                  >
                    Comment
                  </Button>
                  <Button
                    startIcon={<ShareIcon />}
                    sx={{ color: '#65676b', textTransform: 'none', fontWeight: 600, flex: 1 }}
                  >
                    Share
                  </Button>
                </div>

                {/* Admin View Button */}
                <div style={{ borderTop: '1px solid #e4e6eb', padding: '8px 16px' }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(`/post/${post.id}`)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    View Full Post
                  </Button>
                </div>
              </div>
            );
          })}

          {data.length === 0 && !loading && (
            <p style={{ textAlign: 'center', color: '#777' }}>No posts yet</p>
          )}
        </div>
        </PageLayout>
      </div>
    </div>
  );
};

export default Post;
